import React from 'react';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import { cn } from './Input';
import Spinner, { SpinnerSize } from './Spinner';
import { FOCUS_RING_ONLY } from './FieldShell';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'disabled'
    | 'destructive'
    | 'neutral'
    | 'outline'; 
  size?: 'small' | 'medium' | 'large';
  /** Optional so icon-only buttons (pass `icon`, no label) are valid. */
  children?: React.ReactNode;
  ariaLabel?: string;
  tooltip?: string;
  isLoading?: boolean;
  loaderColor?: string;
  icon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'medium',
  onClick,
  disabled,
  children,
  ariaLabel,
  type = 'button',
  className,
  tooltip,
  isLoading = false,
  // Retained so the ~dozen call sites still passing a border-colour class keep
  // type-checking; the loader is now Spinner, which takes its colour from
  // currentColor. Destructured rather than spread so it never reaches the DOM.
  loaderColor: _loaderColor,
  icon,
  ...rest
}, ref) => {
  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 border-transparent dark:bg-white dark:hover:bg-gray-50 dark:text-black',
    secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500 border-transparent',
    tertiary: 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-800 focus:ring-primary-500 dark:focus:ring-primary-500 border-transparent',
    success: 'bg-success-500 text-white hover:bg-success-600 focus:ring-success-500 border-transparent',
    warning: 'bg-warning-500 text-white hover:bg-warning-600 focus:ring-warning-500 border-transparent',
    danger: 'bg-danger-500 text-white hover:bg-danger-600 focus:ring-danger-500 border-transparent',
    disabled: 'bg-disabled-500 text-gray-400 cursor-not-allowed border-transparent',
    destructive: 'border border-hairline bg-surface-sunken/70 text-danger-500 dark:text-danger-400 hover:bg-danger-500 hover:text-white hover:border-danger-500',
    neutral: 'bg-surface-sunken/70 text-fg-strong hover:bg-surface-sunken border border-hairline',
    // No champagne at rest OR on hover - a neutral button is neutral. The only
    // gold a non-primary button carries is its focus ring.
    outline: 'border border-hairline bg-transparent text-fg-body hover:bg-surface-sunken/70 hover:border-hairline transition-all duration-200',
  };

  const sizeClasses = {
    small: 'px-2 py-1 text-xs',
    medium: 'px-4 py-2 text-sm font-sm',
    large: 'px-10 py-4 text-lg font-medium',
  };

  // A button press is not an AI process, so the pending state is the plain
  // Spinner, never the logo loader. Sizes match what BrandLoader rendered
  // before (glyph = the old `xs`).
  const loaderSize = {
    small: 'glyph',
    medium: 'glyph',
    large: 'sm',
  }[size] as SpinnerSize;

  const childCount = React.Children.count(children);
  const justifyClass = childCount > 1 ? 'justify-between' : 'justify-center';

  const classes = cn(
    'flex items-center rounded border transition',
    FOCUS_RING_ONLY,
    variantClasses[variant],
    sizeClasses[size],
    {
      'opacity-50 cursor-not-allowed': disabled || isLoading,
    },
    className
  );

  const content = isLoading ? (
    <span className="flex w-full items-center justify-center">
      <Spinner size={loaderSize} tone="current" />
    </span>
  ) : (
    <span className={clsx('flex items-center w-full', justifyClass, icon && 'gap-2')}>
      {children}
    </span>
  );

  const buttonEl = (
    <button
      ref={ref}
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-label={ariaLabel}
      aria-disabled={disabled || isLoading}
      {...rest}
    >
      {icon && <span className='me-1'>{icon}</span>}
      {content}
    </button>
  );

  // MUI Tooltip doesn't fire on natively disabled buttons (browser blocks hover events).
  // Wrap in a <span> so the tooltip still shows when the button is disabled.
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        {disabled || isLoading ? (
          <span style={{ display: 'inline-block' }}>{buttonEl}</span>
        ) : (
          buttonEl
        )}
      </Tooltip>
    );
  }

  return buttonEl;
});

Button.displayName = 'Button';

export default Button;
